import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Chip,
  Divider,
  TextField,
  makeStyles,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';
import SportScalePrimaryButton from 'common/buttons/sportScalePrimaryButton.component';
import SportScaleSecondaryButton from 'common/buttons/sportScaleSecondaryButton.component';

const SELECT_ALL_VALUE = '__select_all__';
const STORAGE_KEY_SELECTED_FIELDS = 'sportscale.export.selectedFields';

const FIELD_GROUPS = {
  weightChange: {
    title: 'Team Data',
    fields: [
      { key: 'Athlete Name', label: 'Athlete Name', required: true },
      { key: 'Last Name', label: 'Last Name' },
      { key: 'Team Name', label: 'Team Name' },
      { key: 'Weight Date', label: 'Weight Date' },
      { key: 'Weigh Time', label: 'Weigh Time' },
      { key: 'Weight', label: 'Weight' },
      { key: 'Weight Type', label: 'Weight Type' },
      { key: 'Predisposed', label: 'Predisposed' },
      { key: 'Percent Change', label: 'Percent Change' },
      { key: 'Weight Change', label: 'Weight Change' },
      { key: 'Total Tracking Days', label: 'Total Tracking Days' },
      { key: 'Percent Tracked', label: 'Percent Tracked' },
      { key: 'Team Total Tracking Days', label: 'Team Total Tracking Days' },
      { key: 'Athlete ID', label: 'Athlete ID' },
      { key: 'Athlete Note', label: 'Athlete Note' },
    ],
  },
  bmi: {
    title: 'Athlete Data',
    fields: [
      { key: 'Athlete Name', label: 'Athlete Name', required: true },
      { key: 'Last Name', label: 'Last Name' },
      { key: 'Passcode', label: 'Passcode' },
      { key: 'Team Name', label: 'Team Name' },
      { key: 'Roster Name', label: 'Roster Name' },
      { key: 'Height', label: 'Height' },
      { key: 'Weight Date', label: 'Weight Date' },
      { key: 'Weigh Time', label: 'Weigh Time' },
      { key: 'Weight', label: 'Weight' },
      { key: 'BMI', label: 'BMI' },
      { key: 'Weight Type', label: 'Weight Type' },
      { key: 'Gender', label: 'Gender' },
      { key: 'Graduation Year', label: 'Graduation Year' },
      { key: 'DOB', label: 'Date of Birth' },
      { key: 'Ethnicity', label: 'Ethnicity' },
      { key: 'Athlete Note', label: 'Athlete Note' },
      { key: 'Watch List', label: 'Watch List' },
      { key: 'Heat Illness', label: 'Heat Illness' },
      { key: 'Euhydrated', label: 'Euhydrated' },
      { key: 'Predisposition Threshold', label: 'Predisposition Threshold' },
      { key: 'Dehydration Threshold', label: 'Dehydration Threshold' },
      { key: 'IN to IN % CHANGE', label: 'IN to IN % CHANGE' },
      { key: 'IN to IN LBS CHANGE', label: 'IN to IN LBS CHANGE' },
      { key: 'IN to OUT % CHANGE', label: 'IN to OUT % CHANGE' },
      { key: 'IN to OUT LBS CHANGE', label: 'IN to OUT LBS CHANGE' },
      { key: 'Out Over Weight', label: 'Out Over Weight' },
      { key: 'In Over Risk', label: 'In Over Risk' },
      { key: 'In Over Weight', label: 'In Over Weight' },
      { key: 'Athlete ID', label: 'Athlete ID' },
    ],
  },
  targetWeight: {
    title: 'Target Weight Data',
    fields: [
      { key: 'Athlete Name', label: 'Athlete Name', required: true },
      { key: 'Last Name', label: 'Last Name' },
      { key: 'Team Name', label: 'Team Name' },
      { key: 'Roster Name', label: 'Roster Name' },
      { key: 'Height', label: 'Height' },
      { key: 'Gender', label: 'Gender' },
      { key: 'Graduation Year', label: 'Graduation Year' },
      { key: 'DOB', label: 'Date of Birth' },
      { key: 'Ethnicity', label: 'Ethnicity' },
      { key: 'Watch List', label: 'Watch List' },
      { key: 'Initial Weight', label: 'Initial Weight' },
      { key: 'Target Weight', label: 'Target Weight' },
      { key: 'Weight Date', label: 'Weight Date' },
      { key: 'Weight Time', label: 'Weight Time' },
      { key: 'Weight', label: 'Weight' },
      { key: 'LBS to Goal', label: 'LBS to Goal' },
      { key: 'Days to Goal', label: 'Days to Goal' },
      { key: 'Target Created', label: 'Target Created' },
      { key: 'Target Date', label: 'Target Date' },
      { key: 'Achieved', label: 'Achieved' },
      { key: 'Athlete ID', label: 'Athlete ID' },
      { key: 'Athlete Note', label: 'Athlete Note' },
    ],
  },
  wbgt: {
    title: 'WBGT Data',
    fields: [
      { key: 'Team Name', label: 'Team Name', required: true },
      { key: 'name', label: 'Athlete Name' },
      { key: 'initial', label: 'Initial Value' },
      { key: 'peak', label: 'Peak Value' },
      { key: 'Date', label: 'Date' },
    ],
  },
};

const buildInitialFields = availableDataTypes => {
  const initial = {};
  Object.keys(FIELD_GROUPS).forEach(groupKey => {
    if (availableDataTypes.includes(groupKey)) {
      initial[groupKey] = {};
      FIELD_GROUPS[groupKey].fields.forEach(field => {
        initial[groupKey][field.key] = true;
      });
    }
  });
  return initial;
};

const loadStoredFields = availableDataTypes => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_SELECTED_FIELDS);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const merged = {};
    Object.keys(FIELD_GROUPS).forEach(groupKey => {
      if (!availableDataTypes.includes(groupKey)) return;
      merged[groupKey] = {};
      FIELD_GROUPS[groupKey].fields.forEach(field => {
        const storedValue = parsed?.[groupKey]?.[field.key];
        if (field.required) {
          merged[groupKey][field.key] = true;
        } else if (typeof storedValue === 'boolean') {
          merged[groupKey][field.key] = storedValue;
        } else {
          merged[groupKey][field.key] = true;
        }
      });
    });
    return merged;
  } catch (error) {
    return null;
  }
};

const persistFields = selectedFields => {
  try {
    window.localStorage.setItem(STORAGE_KEY_SELECTED_FIELDS, JSON.stringify(selectedFields));
  } catch (error) {
    // ignore storage failures (private mode, quota, etc.)
  }
};

const ExportFieldSelectionDialog = ({
  open,
  onClose,
  onExport,
  availableDataTypes,
  availableSports = [],
  availableAthletes = [],
  defaultFileName = '',
}) => {
  const classes = useStyles();
  const [selectedFields, setSelectedFields] = useState({});
  const [expandedPanels, setExpandedPanels] = useState({});
  const [fileName, setFileName] = useState('');

  const [selectedSports, setSelectedSports] = useState([]);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [dateFrom, setDateFrom] = useState(moment().subtract(30, 'days').toDate());
  const [dateTo, setDateTo] = useState(moment().toDate());

  const sortedSports = useMemo(() => [...availableSports].sort(), [availableSports]);
  const sortedAthletes = useMemo(() => [...availableAthletes].sort(), [availableAthletes]);

  const allSportsSelected = sortedSports.length > 0 && selectedSports.length === sortedSports.length;
  const allAthletesSelected = sortedAthletes.length > 0 && selectedAthletes.length === sortedAthletes.length;

  useEffect(() => {
    if (!open) return;
    const stored = loadStoredFields(availableDataTypes);
    setSelectedFields(stored || buildInitialFields(availableDataTypes));

    const initialExpanded = {};
    Object.keys(FIELD_GROUPS).forEach(groupKey => {
      if (availableDataTypes.includes(groupKey)) initialExpanded[groupKey] = false;
    });
    setExpandedPanels(initialExpanded);

    setSelectedSports(sortedSports);
    setSelectedAthletes(sortedAthletes);
    setDateFrom(moment().subtract(30, 'days').toDate());
    setDateTo(moment().toDate());
    setFileName(defaultFileName);
  }, [open, availableDataTypes, sortedSports, sortedAthletes, defaultFileName]);

  const handleFieldToggle = (groupKey, fieldKey) => {
    setSelectedFields(prev => ({
      ...prev,
      [groupKey]: {
        ...prev[groupKey],
        [fieldKey]: !prev[groupKey]?.[fieldKey],
      },
    }));
  };

  const handleSelectAllFields = groupKey => {
    setSelectedFields(prev => ({
      ...prev,
      [groupKey]: FIELD_GROUPS[groupKey].fields.reduce((acc, field) => {
        acc[field.key] = true;
        return acc;
      }, {}),
    }));
  };

  const handleDeselectAllFields = groupKey => {
    setSelectedFields(prev => ({
      ...prev,
      [groupKey]: FIELD_GROUPS[groupKey].fields.reduce((acc, field) => {
        acc[field.key] = field.required || false;
        return acc;
      }, {}),
    }));
  };

  const handlePanelToggle = groupKey => {
    setExpandedPanels(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleSportsChange = event => {
    const value = event.target.value;
    if (value.includes(SELECT_ALL_VALUE)) {
      setSelectedSports(allSportsSelected ? [] : sortedSports);
      return;
    }
    setSelectedSports(value);
  };

  const handleAthletesChange = event => {
    const value = event.target.value;
    if (value.includes(SELECT_ALL_VALUE)) {
      setSelectedAthletes(allAthletesSelected ? [] : sortedAthletes);
      return;
    }
    setSelectedAthletes(value);
  };

  const handleExport = () => {
    persistFields(selectedFields);
    onExport(
      selectedFields,
      {
        sports: selectedSports,
        athletes: selectedAthletes,
        dateFrom,
        dateTo,
      },
      fileName.trim()
    );
    onClose();
  };

  const getSelectedCount = groupKey => Object.values(selectedFields[groupKey] || {}).filter(Boolean).length;
  const getTotalCount = groupKey => FIELD_GROUPS[groupKey].fields.length;

  const renderMultiSelect = ({ label, value, options, allSelected, onChange, emptyText }) => (
    <FormControl variant="outlined" fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={value}
        onChange={onChange}
        renderValue={selected => {
          if (selected.length === 0) return <em>{emptyText}</em>;
          if (allSelected) return <em>All ({options.length})</em>;
          return (
            <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
              {selected.map(v => (
                <Chip key={v} label={v} size="small" />
              ))}
            </Box>
          );
        }}
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          PaperProps: { style: { maxHeight: 320 } },
        }}
      >
        <MenuItem value={SELECT_ALL_VALUE}>
          <Checkbox checked={allSelected} indeterminate={!allSelected && value.length > 0} color="primary" />
          <ListItemText primary={allSelected ? 'Deselect All' : 'Select All'} />
        </MenuItem>
        <Divider />
        {options.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary={emptyText} />
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={value.includes(option)} color="primary" />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Export Excel Spreadsheet</Typography>
        <Typography variant="body2" color="textSecondary">
          Filter the data and choose which fields to include in your export
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.filtersSection}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Date From"
                value={dateFrom}
                onChange={date => setDateFrom(date ? date.toDate() : null)}
                format="MM/DD/YYYY"
                inputVariant="outlined"
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Date To"
                value={dateTo}
                onChange={date => setDateTo(date ? date.toDate() : null)}
                format="MM/DD/YYYY"
                inputVariant="outlined"
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMultiSelect({
                label: 'Sport',
                value: selectedSports,
                options: sortedSports,
                allSelected: allSportsSelected,
                onChange: handleSportsChange,
                emptyText: 'No sports available',
              })}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMultiSelect({
                label: 'Athlete',
                value: selectedAthletes,
                options: sortedAthletes,
                allSelected: allAthletesSelected,
                onChange: handleAthletesChange,
                emptyText: 'No athletes available',
              })}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="File Name"
                value={fileName}
                onChange={event => setFileName(event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                helperText='".xlsx" will be added automatically. Leave blank to use the default name.'
              />
            </Grid>
          </Grid>
        </Box>

        <Divider className={classes.divider} />

        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Fields
        </Typography>
        {Object.keys(FIELD_GROUPS).map(groupKey => {
          if (!availableDataTypes.includes(groupKey)) return null;

          const group = FIELD_GROUPS[groupKey];
          const selectedCount = getSelectedCount(groupKey);
          const totalCount = getTotalCount(groupKey);

          return (
            <Accordion
              key={groupKey}
              expanded={!!expandedPanels[groupKey]}
              onChange={() => handlePanelToggle(groupKey)}
              className={classes.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box className={classes.accordionHeader}>
                  <Typography variant="subtitle1" className={classes.groupTitle}>
                    {group.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedCount} of {totalCount} fields selected
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Box className={classes.fieldGroup}>
                  <Box className={classes.groupActions}>
                    <Button
                      size="small"
                      onClick={() => handleSelectAllFields(groupKey)}
                      className={classes.actionButton}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleDeselectAllFields(groupKey)}
                      className={classes.actionButton}
                    >
                      Deselect All
                    </Button>
                  </Box>
                  <Box className={classes.fieldsContainer}>
                    {group.fields.map(field => (
                      <FormControlLabel
                        key={field.key}
                        control={
                          <Checkbox
                            checked={selectedFields[groupKey]?.[field.key] || false}
                            onChange={() => handleFieldToggle(groupKey, field.key)}
                            disabled={field.required}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              {field.label}
                              {field.required && (
                                <Typography component="span" color="error" className={classes.required}>
                                  {' '}
                                  *
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        }
                        className={classes.fieldLabel}
                      />
                    ))}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <SportScaleSecondaryButton onClick={onClose}>Cancel</SportScaleSecondaryButton>
        <SportScalePrimaryButton onClick={handleExport}>Export</SportScalePrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: theme.spacing(2),
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  filtersSection: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
  },
  accordionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingRight: theme.spacing(2),
  },
  groupTitle: {
    fontWeight: 'bold',
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(1, 2, 2, 2),
  },
  fieldGroup: {
    width: '100%',
  },
  groupActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    textTransform: 'none',
    fontSize: '0.75rem',
  },
  fieldsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: theme.spacing(1),
  },
  fieldLabel: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
    },
  },
  required: {
    fontWeight: 'bold',
  },
  dialogActions: {
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
}));

export default ExportFieldSelectionDialog;
